import React, { useEffect, useState } from 'react';

import Button, { LoadingButton } from '@atlaskit/button';
import { IconButton } from '@atlaskit/button/new';
import TrashIcon from '@atlaskit/icon/glyph/trash';
import Modal, {
  ModalHeader,
  ModalTitle,
  ModalTransition,
} from '@atlaskit/modal-dialog';
import Tooltip from '@atlaskit/tooltip';

import { Label, ModalBody, ModalFooter } from '@qality/common-components';
import { ExecutionStatusSelect } from '@qality/common-components/src/select/execution-status-select/ExecutionStatusSelect';
import { ExecutionStatus } from '@qality/common-model';
import { FlagService } from '@qality/common-service';

import { useTestCaseExecutionStatusPageContext } from '../../TestCaseExecutionStatusPageProvider';
import {
  ModalBodyContainerStyled,
  ModalTextStyled,
} from './DeleteStatus.styled';

type Props = {
  status: ExecutionStatus;
  isVisible?: boolean;
};

const LAST_STATUS_INFO =
  'You can’t delete this status. Minimum 1 status is required.';

export default function DeleteStatusModal({
  status,
  isVisible = true,
}: Props): JSX.Element {
  const { statuses, deleteStatus } = useTestCaseExecutionStatusPageContext();

  const filteredStatuses = statuses.filter((arg) => arg.id !== status.id);
  const isLastStatus = statuses.length === 1;

  const [isLoading, setIsLoading] = useState(false);
  const [alternativeStatus, setAlternativeStatus] = useState<ExecutionStatus>(
    filteredStatuses[0]
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    setAlternativeStatus(filteredStatuses[0]);
  }, [statuses]);

  const handleDeleteStatus = async () => {
    try {
      setIsLoading(true);
      await deleteStatus(status, alternativeStatus);
      FlagService.createSuccessFlag('Success', 'Successfully deleted status');
    } catch (error) {
      FlagService.createErrorFlag(
        'Error',
        'We could not delete selected status. ' +
          'Please try again. If the problem persists, feel free to contact our support.'
      );
    } finally {
      setIsLoading(false);
      setIsModalOpen(false);
    }
  };

  return (
    <>
      {isVisible && (
        <Tooltip content={isLastStatus && LAST_STATUS_INFO}>
          <IconButton
            icon={TrashIcon}
            color='var(--ds-icon, var(--ds-fb-icon))'
            onClick={() => setIsModalOpen(true)}
            label='delete-status'
            isDisabled={isLastStatus}
          />
        </Tooltip>
      )}

      {isModalOpen && (
        <ModalTransition>
          <Modal
            onClose={() => setIsModalOpen(false)}
            shouldCloseOnOverlayClick={false}
            shouldCloseOnEscapePress
            shouldScrollInViewport
            width={400}
          >
            <ModalHeader>
              <ModalTitle appearance='danger'>Delete status</ModalTitle>
            </ModalHeader>
            <ModalBody>
              <ModalBodyContainerStyled>
                <ModalTextStyled>
                  If you want to delete status, you have to pick another status
                  that will replace it (all test case execution curently in the
                  status you want to delete will be changed to the one you
                  select below).
                </ModalTextStyled>
                <Label text='Alternative status' isRequired />
                <ExecutionStatusSelect
                  isLoading={isLoading}
                  value={alternativeStatus}
                  statuses={filteredStatuses}
                  onChange={setAlternativeStatus}
                />
              </ModalBodyContainerStyled>
            </ModalBody>
            <ModalFooter>
              <Button appearance='subtle' onClick={() => setIsModalOpen(false)}>
                Cancel
              </Button>
              <LoadingButton
                isLoading={isLoading}
                appearance='danger'
                onClick={handleDeleteStatus}
              >
                Delete status
              </LoadingButton>
            </ModalFooter>
          </Modal>
        </ModalTransition>
      )}
    </>
  );
}
